/* eslint-disable react/destructuring-assignment */

import React, { useCallback, useRef } from 'react';
import { TextField, IconButton } from '@mui/material';
import { ReactComponent as SearchIconNew } from '../../assets/SearchIconNew.svg';

/**
 * It's a custom text field that takes in props and returns a text field with a search icon.
 * @param {object} props - props accept all that support in TextField
 * @returns A React component.
 * @example <TransperentSearchBox placeholder="Search" />
 */

const SearchBox = (props) => {
  const liveRegionRef = useRef(null);

  /**
   * @function handleFocus
   * Handles focus event by setting the live region text to the aria-label when focused.
   */
  const handleFocus = useCallback(() => {
    if (liveRegionRef.current && props['aria-label']) {
      liveRegionRef.current.innerText = props['aria-label'];
    }
  }, [props['aria-label']]); // Dependencies: only changes when aria-label changes

  /**
   * @function handleBlur
   * Handles blur event by clearing the live region text when focus is lost.
   */
  const handleBlur = useCallback(() => {
    if (liveRegionRef.current) {
      liveRegionRef.current.innerText = '';
    }
  }, []); // No dependencies, runs only on blur

  const textFieldSx = useCallback(
    () => ({
      padding: '0px',
      width: '200px',
      marginTop: '0px',
    }),
    []
  );

  return (
    <>
      <TextField
        sx={textFieldSx}
        variant="filled"
        InputProps={{
          'aria-label': props['aria-label'], // Ensure aria-label is set
          'aria-describedby': props['aria-describedby'], // Provide additional context if needed
          startAdornment: (
            <IconButton
              size="small"
              aria-hidden="true"
              tabIndex={-1}
              color="primary"
            >
              <SearchIconNew aria-hidden="true" />
            </IconButton>
          ),
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
      {/* Live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        role="alert"
        aria-live="assertive"
        style={{
          width: '0px',
          height: '0px',
          overflow: 'hidden',
        }}
      />
    </>
  );
};

export default SearchBox;
